import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { isAuthenticatedViaCookie, logout } from '../../utils/utils';
import { logoNameMap } from '../../assets/SVGs/logos/Logo';

interface UserInfo {
  name: string;
  gn: string;
  email?: string;
  phone?: string;
  guarantorLastName?: string;
}

interface SidebarProps {
  userInfo: UserInfo | null;
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ userInfo, isOpen, onClose }) => {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const IconComponent = logoNameMap[theme.logo];
  const [localStorageUserInfo, setLocalStorageUserInfo] = useState<UserInfo | null>(null);

  // Check if user is logged in and get data from local storage when userInfo is missing
  useEffect(() => {
    if (!userInfo && isAuthenticatedViaCookie()) {
      const firstName = localStorage.getItem('firstName') || '';
      const lastName = localStorage.getItem('lastName') || '';
      
      if (firstName && lastName) {
        setLocalStorageUserInfo({
          name: `${firstName} ${lastName}`,
          gn: '', // Don't show guarantor number
          email: localStorage.getItem('email') || undefined,
          phone: localStorage.getItem('phone') || undefined,
          // Don't include guarantorLastName
        });
      }
    }
  }, [userInfo]);

  // Use local storage data if userInfo is not available but user is authenticated
  const displayUserInfo = userInfo || localStorageUserInfo;

  const handleNavigation = (path: string) => {
    if (location.pathname !== path) {
      navigate(path);
    }
    onClose(); // Close sidebar after navigation
  };

  const handleLogout = () => {
    logout();
    onClose(); // Close sidebar after logout
  };

  const isCurrentPage = (path: string) => location.pathname === path;

  // Navigation items based on login status
  const navigationItems = isAuthenticatedViaCookie() ? [
    { path: '/', label: 'Payments', icon: '💳' },
    { path: '/payment-history', label: 'Payment History', icon: '📊' },
    { path: '/subscriptions', label: 'Subscriptions', icon: '🔄' },
  ] : [];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: '300px' }}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Header Section */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center mb-2">
              <div className="flex-shrink-0">
                <IconComponent width={80} />
              </div>
              <div className="ml-3">
                <h1 className="text-lg font-bold text-gray-900" data-testid="hospital-name">
                  St. Luke Hospital
                </h1>
                <p className="text-sm text-gray-600" data-testid="portal-subtitle">
                  Patient Portal
                </p>
              </div>
            </div>
          </div>

          {/* User Info Section */}
          {displayUserInfo && (
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-gray-600 font-semibold text-lg">
                    {displayUserInfo.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900" data-testid="user-name">
                    {displayUserInfo.name}
                  </h2>
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600">
                {/* Only show guarantor info if userInfo is available (not from local storage) */}
                {userInfo && userInfo.gn && (
                  <div>
                    <span className="font-medium">Guarantor Number:</span>
                    <span className="ml-2 font-semibold text-gray-900" data-testid="guarantor-number">
                      {userInfo.gn}
                    </span>
                  </div>
                )}
                {userInfo && userInfo.guarantorLastName && (
                  <div>
                    <span className="font-medium">Guarantor Last Name:</span>
                    <span className="ml-2 font-semibold text-gray-900" data-testid="guarantor-lastname">
                      {userInfo.guarantorLastName}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Menu */}
          <div className="flex-1 p-6">
            <nav className="space-y-2">
              {navigationItems.length > 0 ? (
                navigationItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors duration-200 ${
                      isCurrentPage(item.path)
                        ? (theme.client === 'stluke'
                            ? 'bg-[#F2F5F9] text-[#71001F] border-l-4 border-[#71001F]'
                            : 'bg-blue-50 text-blue-700 border-l-4 border-blue-700')
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {/* <span className="text-2xl mr-3">{item.icon}</span> */}
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p className="text-sm">Please login to access navigation</p>
                </div>
              )}
            </nav>
          </div>

          {/* Footer Section */}
          <div className="p-6 border-t border-gray-200">
            {isAuthenticatedViaCookie() ? (
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-3 text-left text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors duration-200"
                data-testid="logout-button"
              >
                {/* <span className="text-2xl mr-3">🚪</span> */}
                <span className="font-medium">Logout</span>
              </button>
            ) : (
              <button
                onClick={() => handleNavigation('/login')}
                className={`w-full flex items-center px-4 py-3 text-left text-gray-700 rounded-lg transition-colors duration-200 ${
                  theme.client === 'stluke' ? 'hover:bg-[#F2F5F9] hover:text-[#71001F]' : 'hover:bg-blue-50 hover:text-blue-700'
                }`}
                data-testid="login-button"
              >
                <span className="text-2xl mr-3">🔑</span>
                <span className="font-medium">Login</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
